import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('ckb_session');
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);
        setUser(sessionData);
      } catch (e) {
        localStorage.removeItem('ckb_session');
      }
    }
    setIsLoading(false);
  }, []);

  // Get all registered users from localStorage
  const getUsers = () => {
    const users = localStorage.getItem('ckb_users');
    return users ? JSON.parse(users) : [];
  };

  // Save users to localStorage
  const saveUsers = (users) => {
    localStorage.setItem('ckb_users', JSON.stringify(users));
  };

  // Register a new user
  const register = (firstName, lastName, email, password) => {
    const users = getUsers();
    
    // Check if email already exists
    const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (existingUser) {
      return { success: false, error: 'An account with this email already exists.' };
    }

    // Validate password
    if (password.length < 6) {
      return { success: false, error: 'Password must be at least 6 characters.' };
    }

    // Create new user
    const newUser = {
      id: Date.now().toString(),
      firstName,
      lastName,
      email: email.toLowerCase(),
      password, // In a real app, this would be hashed
      createdAt: new Date().toISOString()
    };

    users.push(newUser);
    saveUsers(users);

    // Auto login after registration
    const sessionData = {
      id: newUser.id,
      firstName: newUser.firstName,
      lastName: newUser.lastName,
      email: newUser.email
    };
    setUser(sessionData);
    localStorage.setItem('ckb_session', JSON.stringify(sessionData));

    return { success: true };
  };

  // Login user
  const login = (email, password) => {
    const users = getUsers();
    
    const foundUser = users.find(
      u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );

    if (!foundUser) {
      return { success: false, error: 'Invalid email or password.' };
    }

    const sessionData = {
      id: foundUser.id,
      firstName: foundUser.firstName,
      lastName: foundUser.lastName,
      email: foundUser.email
    };
    setUser(sessionData);
    localStorage.setItem('ckb_session', JSON.stringify(sessionData));

    return { success: true };
  };

  // Logout user
  const logout = () => {
    setUser(null);
    localStorage.removeItem('ckb_session');
  };

  // Request password reset (simulated)
  const requestPasswordReset = (email) => {
    const users = getUsers();
    const foundUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());

    if (!foundUser) {
      // For security, we don't reveal if email exists
      return { success: true, message: 'If an account with this email exists, you will receive a password reset link.' };
    }

    // In a real app, this would send an email
    return { success: true, message: 'If an account with this email exists, you will receive a password reset link.' };
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    requestPasswordReset
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
